import React, { useState } from 'react';
import {
  Container,
  CheckboxLabel,
  DateRangeWrapper,
  DateInputWrapper,
  Separator,
  ShortcutButton,
  ButtonsWrapper,
  ErrorFormMessageStyled,
  CenteredErrorFormMessageStyled,
  DateFieldsWrapper
} from './styled';
import Datepicker from 'components/Inputs/DatePicker';
import { Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

const DateRangeFilter = ({
  onChange,
  setValue,
  disabled = false,
  control,
  errors,
  getValues,
  clearErrors
}) => {
  const [enabled, setEnabled] = useState(false);

  const parseBrazilianDate = (dateString) => {
    if (!dateString) return null;
    if (dateString instanceof Date) return dateString;
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(year, month - 1, day);
    }
    return new Date(dateString);
  };

  const formatToBrazilianDate = (date) => {
    if (!date || !(date instanceof Date)) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleShortcut = (days) => {
    if (disabled) return;
    const today = new Date();
    let start = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    let end = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    switch (days) {
      case 1:
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 7:
        start.setDate(start.getDate() - 6);
        break;
      case 30:
        start.setDate(start.getDate() - 29);
        break;
      default:
        break;
    }

    if (!enabled) setEnabled(true);

    const startFormatted = formatToBrazilianDate(start);
    const endFormatted = formatToBrazilianDate(end);

    setValue('dateBegin', startFormatted, { shouldValidate: true });
    setValue('dateEnd', endFormatted, { shouldValidate: true });

    if (onChange && typeof onChange === 'function') {
      onChange({ dateBegin: startFormatted, dateEnd: endFormatted });
    }
  };

  const handleCheckbox = () => {
    if (disabled) return;
    const newEnabled = !enabled;
    setEnabled(newEnabled);

    if (!newEnabled) {
      setValue('dateBegin', '', { shouldValidate: false });
      setValue('dateEnd', '', { shouldValidate: false });
      clearErrors(['dateBegin', 'dateEnd']);
      if (onChange && typeof onChange === 'function') {
        onChange({ dateBegin: '', dateEnd: '' });
      }
    }
  };

  const validateDateBegin = (value) => {
    if (!enabled) return true;
    if (!value) return 'Data inicial é obrigatória';
    const parsedDate = parseBrazilianDate(value);
    if (!parsedDate || isNaN(parsedDate.getTime())) {
      return 'Data inicial inválida';
    }
    return true;
  };

  const validateDateEnd = (value) => {
    if (!enabled) return true;
    if (!value) return 'Data final é obrigatória';
    const parsedDate = parseBrazilianDate(value);
    if (!parsedDate || isNaN(parsedDate.getTime())) {
      return 'Data final inválida';
    }

    const dateBegin = getValues('dateBegin');
    if (dateBegin) {
      const startDate = parseBrazilianDate(dateBegin);
      if (startDate && !isNaN(startDate.getTime()) && parsedDate < startDate) {
        return 'Data final deve ser igual ou posterior à data inicial';
      }
    }
    return true;
  };

  const hasRangeError =
    errors.dateEnd?.message ===
    'Data final deve ser igual ou posterior à data inicial';

  return (
    <Container>
      <CheckboxLabel disabled={disabled}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={handleCheckbox}
          disabled={disabled}
        />
        Buscar por Período
      </CheckboxLabel>

      <DateRangeWrapper>
        <ButtonsWrapper>
          <ShortcutButton
            onClick={() => handleShortcut(0)}
            disabled={disabled || !enabled}
          >
            Hoje
          </ShortcutButton>
          <ShortcutButton
            onClick={() => handleShortcut(1)}
            disabled={disabled || !enabled}
          >
            Ontem
          </ShortcutButton>
          <ShortcutButton
            onClick={() => handleShortcut(7)}
            disabled={disabled || !enabled}
          >
            Últimos 7 dias
          </ShortcutButton>
          <ShortcutButton
            onClick={() => handleShortcut(30)}
            disabled={disabled || !enabled}
          >
            Últimos 30 dias
          </ShortcutButton>
        </ButtonsWrapper>
        <DateFieldsWrapper>
          <DateInputWrapper>
            <Controller
              render={(props) => (
                <Datepicker
                  fieldName="dateBegin"
                  valueController={setValue}
                  defaultValue={props.field?.value || props.value}
                  shouldDisable={!enabled}
                  aria-describedby={
                    hasRangeError ? 'dateRange-error' : undefined
                  }
                />
              )}
              control={control}
              name="dateBegin"
              rules={{ validate: validateDateBegin }}
            />
            <ErrorMessage
              errors={errors}
              name="dateBegin"
              render={({ message }) => (
                <ErrorFormMessageStyled>{message}</ErrorFormMessageStyled>
              )}
            />
          </DateInputWrapper>

          <Separator>—</Separator>

          <DateInputWrapper>
            <Controller
              render={(props) => (
                <Datepicker
                  fieldName="dateEnd"
                  valueController={setValue}
                  defaultValue={props.field?.value || props.value}
                  shouldDisable={!enabled}
                  aria-describedby={
                    hasRangeError ? 'dateRange-error' : undefined
                  }
                />
              )}
              control={control}
              name="dateEnd"
              rules={{ validate: validateDateEnd }}
            />
            <ErrorMessage
              errors={errors}
              name="dateEnd"
              render={({ message }) =>
                message !==
                  'Data final deve ser igual ou posterior à data inicial' && (
                  <ErrorFormMessageStyled>{message}</ErrorFormMessageStyled>
                )
              }
            />
          </DateInputWrapper>
        </DateFieldsWrapper>

        {hasRangeError && (
          <CenteredErrorFormMessageStyled id="dateRange-error">
            {errors.dateEnd.message}
          </CenteredErrorFormMessageStyled>
        )}
      </DateRangeWrapper>
    </Container>
  );
};

export default DateRangeFilter;
